/**
 * Mantém a faixa azul do painel lateral direito (desktop largo) SEMPRE na mesma altura da
 * faixa azul da tela ao lado — inclusive enquanto a tela carrega (skeleton da Home,
 * skeleton de carregamento de rota) e em telas com cabeçalho próprio (Notificações).
 *
 * Em vez de cada cabeçalho avisar a própria altura (o que só acontecia depois de a tela
 * montar), o layout observa a área de conteúdo e mede, antes de cada pintura, onde termina
 * o bloco azul que está no topo dela. Publica em `--altura-cabecalho-pagina` (no :root).
 */
import { type RefObject, useLayoutEffect } from "react";
import { useMediaQuery } from "@/ui/useMediaQuery";

const AZUL = "rgb(41, 73, 86)";

function alturaFaixa(main: HTMLElement): number | null {
  const r = main.getBoundingClientRect();
  if (r.width < 10) return null;
  let el = document.elementFromPoint(r.left + 6, r.top + 6);
  while (el && el !== main && main.contains(el)) {
    const er = el.getBoundingClientRect();
    if (getComputedStyle(el).backgroundColor === AZUL && er.width >= r.width - 4) return Math.round(er.bottom - r.top);
    el = el.parentElement;
  }
  return null;
}

export function useFaixaSincronizada(mainRef: RefObject<HTMLElement | null>) {
  const ativo = useMediaQuery("(min-width: 1440px)");
  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!ativo || !main) return;
    let ultimo = -1;
    let quadro = 0;
    const medir = () => {
      if (quadro) {
        cancelAnimationFrame(quadro);
        quadro = 0;
      }
      const h = alturaFaixa(main);
      if (h == null || h === ultimo) return;
      ultimo = h;
      document.documentElement.style.setProperty("--altura-cabecalho-pagina", `${h}px`);
    };
    // Mede NA HORA: o MutationObserver (microtask depois do commit) e o ResizeObserver
    // (depois do layout) rodam antes da pintura — as duas faixas mudam no mesmo quadro.
    // O rAF fica só como rede de segurança para mudanças sem mutação (ex.: fontes).
    const agendar = () => {
      medir();
      if (!quadro) quadro = requestAnimationFrame(medir);
    };
    medir();
    const mo = new MutationObserver(agendar);
    mo.observe(main, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "style"] });
    const ro = new ResizeObserver(agendar);
    ro.observe(main);
    window.addEventListener("resize", agendar);
    return () => {
      mo.disconnect();
      ro.disconnect();
      window.removeEventListener("resize", agendar);
      if (quadro) cancelAnimationFrame(quadro);
    };
  }, [ativo, mainRef]);
}
